import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const inp = "w-full border border-linen bg-white px-3 py-2.5 text-sm outline-none focus:border-pitch";
const lbl = "eyebrow block mb-2";
const saveContent = async (key, data, msg = "Saved") => {
  try { await api.put(`/admin/content/${key}`, { data }); toast.success(msg); }
  catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
};

function HomepageEditor() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/content/homepage").then((r) => setD(r.data.data || {})).catch(() => {}); }, []);
  if (!d) return <div className="text-stonemuted">Loading…</div>;
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const setPoint = (i, k) => (e) => { const pts = [...(d.standard_points || [])]; pts[i] = { ...pts[i], [k]: e.target.value }; setD({ ...d, standard_points: pts }); };
  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white border border-linen p-6 space-y-4">
        <h3 className="font-serif text-xl text-pitch">Hero</h3>
        <div><label className={lbl}>Heading</label><input value={d.hero_heading || ""} onChange={set("hero_heading")} className={inp} data-testid="cms-hero-heading" /></div>
        <div><label className={lbl}>Subtext</label><textarea rows={2} value={d.hero_subtext || ""} onChange={set("hero_subtext")} className={inp} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Primary Button</label><input value={d.hero_primary_cta || ""} onChange={set("hero_primary_cta")} className={inp} /></div>
          <div><label className={lbl}>Secondary Button</label><input value={d.hero_secondary_cta || ""} onChange={set("hero_secondary_cta")} className={inp} /></div>
        </div>
        <div><label className={lbl}>Hero Image URL</label><input value={d.hero_image || ""} onChange={set("hero_image")} className={inp} /></div>
      </div>
      <div className="bg-white border border-linen p-6 space-y-4">
        <h3 className="font-serif text-xl text-pitch">The LUXYREIGN Standard</h3>
        {(d.standard_points || []).map((p, i) => (
          <div key={i} className="grid grid-cols-3 gap-3">
            <input value={p.title} onChange={setPoint(i, "title")} className={inp} placeholder="Title" />
            <input value={p.text} onChange={setPoint(i, "text")} className={`${inp} col-span-2`} placeholder="Text" />
          </div>
        ))}
      </div>
      <div className="bg-white border border-linen p-6 space-y-4">
        <h3 className="font-serif text-xl text-pitch">Authentication & About</h3>
        <div><label className={lbl}>Authentication Heading</label><input value={d.authentication_heading || ""} onChange={set("authentication_heading")} className={inp} /></div>
        <div><label className={lbl}>Authentication Text</label><textarea rows={4} value={d.authentication_text || ""} onChange={set("authentication_text")} className={inp} data-testid="cms-auth-text" /></div>
        <div><label className={lbl}>About Heading</label><input value={d.about_heading || ""} onChange={set("about_heading")} className={inp} /></div>
        <div><label className={lbl}>About Text</label><textarea rows={4} value={d.about_text || ""} onChange={set("about_text")} className={inp} /></div>
      </div>
      <button onClick={() => saveContent("homepage", d, "Homepage updated")} data-testid="cms-save-homepage"
        className="inline-flex items-center gap-2 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4"><Save className="w-4 h-4" /> Save Homepage</button>
    </div>
  );
}

function FAQEditor() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({ category: "Authenticity", question: "", answer: "", order: 0 });
  const load = () => api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/faqs", form); toast.success("FAQ added"); setForm({ ...form, question: "", answer: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/faqs/${id}`); load(); } catch {} };
  return (
    <div className="max-w-2xl space-y-6">
      <form onSubmit={add} className="bg-white border border-linen p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp} placeholder="Category" data-testid="faq-category" />
          <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className={inp} placeholder="Order" />
        </div>
        <input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className={inp} placeholder="Question" data-testid="faq-question" />
        <textarea required rows={3} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className={inp} placeholder="Answer" data-testid="faq-answer" />
        <button type="submit" className="inline-flex items-center gap-2 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.15em] px-6 py-3"><Plus className="w-4 h-4" /> Add FAQ</button>
      </form>
      <div className="bg-white border border-linen divide-y divide-linen">
        {faqs.map((f) => (
          <div key={f.id} className="p-4 flex gap-4">
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-champagne-dark">{f.category}</div>
              <div className="font-medium text-pitch">{f.question}</div>
              <div className="text-sm text-charcoal/70 mt-1">{f.answer}</div>
            </div>
            <button onClick={() => del(f.id)} className="p-2 text-stonemuted hover:text-vermilion self-start"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShippingEditor() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/content/shipping").then((r) => setD(r.data.data || {})).catch(() => {}); }, []);
  if (!d) return <div className="text-stonemuted">Loading…</div>;
  const set = (k, num) => (e) => setD({ ...d, [k]: num ? parseFloat(e.target.value) || 0 : e.target.value });
  return (
    <div className="max-w-xl bg-white border border-linen p-6 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className={lbl}>Domestic Price (AUD)</label><input type="number" value={d.price ?? ""} onChange={set("price", true)} className={inp} data-testid="ship-price" /></div>
        <div><label className={lbl}>Free Shipping Threshold</label><input type="number" value={d.free_threshold ?? ""} onChange={set("free_threshold", true)} className={inp} /></div>
        <div><label className={lbl}>International Price</label><input type="number" value={d.intl_price ?? ""} onChange={set("intl_price", true)} className={inp} /></div>
        <div><label className={lbl}>Method</label><input value={d.method || ""} onChange={set("method")} className={inp} /></div>
      </div>
      <div><label className={lbl}>Estimated Delivery</label><input value={d.estimate || ""} onChange={set("estimate")} className={inp} /></div>
      <button onClick={() => saveContent("shipping", d, "Shipping updated")} className="inline-flex items-center gap-2 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4"><Save className="w-4 h-4" /> Save Shipping</button>
    </div>
  );
}

const PAGE_KEYS = ["about", "privacy", "terms", "returns", "shipping_policy", "authentication_policy", "cookie", "contact"];
function PagesEditor() {
  const [key, setKey] = useState("privacy");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/admin/content/${key}`).then((r) => setD(r.data.data || {})).catch(() => setD({})); }, [key]);
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  return (
    <div className="max-w-2xl space-y-4">
      <select value={key} onChange={(e) => setKey(e.target.value)} className={`${inp} max-w-xs`} data-testid="pages-select">
        {PAGE_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      {!d ? <div className="text-stonemuted">Loading…</div> : (
        <div className="bg-white border border-linen p-6 space-y-4">
          {key === "contact" ? (
            <>
              <div><label className={lbl}>Email</label><input value={d.email || ""} onChange={set("email")} className={inp} /></div>
              <div><label className={lbl}>Phone</label><input value={d.phone || ""} onChange={set("phone")} className={inp} /></div>
              <div><label className={lbl}>Hours</label><input value={d.hours || ""} onChange={set("hours")} className={inp} /></div>
              <div><label className={lbl}>Instagram</label><input value={d.instagram || ""} onChange={set("instagram")} className={inp} /></div>
              <div><label className={lbl}>Note</label><input value={d.note || ""} onChange={set("note")} className={inp} /></div>
            </>
          ) : (
            <>
              <div><label className={lbl}>Title</label><input value={d.title || ""} onChange={set("title")} className={inp} /></div>
              <div><label className={lbl}>Body</label><textarea rows={10} value={d.body || ""} onChange={set("body")} className={inp} data-testid="page-body" /></div>
            </>
          )}
          <button onClick={() => saveContent(key, d, "Page updated")} className="inline-flex items-center gap-2 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4"><Save className="w-4 h-4" /> Save Page</button>
        </div>
      )}
    </div>
  );
}

function PaymentsEditor() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/content/payments").then((r) => setD(r.data.data || {})).catch(() => setD({})); }, []);
  if (!d) return <div className="text-stonemuted">Loading…</div>;
  const bank = d.bank || {};
  const setB = (k) => (e) => setD({ ...d, bank: { ...bank, [k]: e.target.value } });
  const toggle = (k) => () => setD({ ...d, [k]: !d[k] });
  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white border border-linen p-6 space-y-4">
        <h3 className="font-serif text-xl text-pitch">Methods & Reservation</h3>
        <label className="flex items-center justify-between py-1">
          <span className="text-sm">Accept <strong>PayPal</strong> (card & PayPal balance)</span>
          <input type="checkbox" checked={!!d.paypal_enabled} onChange={toggle("paypal_enabled")} data-testid="pay-toggle-paypal" className="w-4 h-4 accent-pitch" />
        </label>
        <label className="flex items-center justify-between py-1 border-t border-linen pt-4">
          <span className="text-sm">Accept <strong>Bank Transfer</strong></span>
          <input type="checkbox" checked={!!d.bank_enabled} onChange={toggle("bank_enabled")} data-testid="pay-toggle-bank" className="w-4 h-4 accent-pitch" />
        </label>
        <div className="border-t border-linen pt-4">
          <label className={lbl}>Reservation Window (minutes)</label>
          <input type="number" min={5} value={d.reservation_minutes ?? 60}
            onChange={(e) => setD({ ...d, reservation_minutes: parseInt(e.target.value) || 60 })}
            className={`${inp} max-w-[200px]`} data-testid="pay-reservation-minutes" />
          <p className="text-[11px] text-stonemuted mt-1.5">How long a one-of-one piece is held for an unpaid order before it is automatically released back to inventory.</p>
        </div>
        <div className="border-t border-linen pt-4">
          <label className={lbl}>Store Owner Notification Email</label>
          <input type="email" value={d.owner_email || ""}
            onChange={(e) => setD({ ...d, owner_email: e.target.value })}
            className={inp} placeholder="owner@luxyreign.com" data-testid="pay-owner-email" />
          <p className="text-[11px] text-stonemuted mt-1.5">Receives a notification for every order: on payment confirmation (PayPal/card) and on placement + confirmation for bank transfers. Leave blank to fall back to the admin email.</p>
        </div>
        <p className="text-[11px] text-stonemuted border-t border-linen pt-4">PayPal API keys are configured securely on the server. Toggle above simply enables or hides PayPal at checkout.</p>
      </div>

      <div className="bg-white border border-linen p-6 space-y-4">
        <h3 className="font-serif text-xl text-pitch">Bank Transfer Details</h3>
        <p className="text-[11px] text-stonemuted -mt-2">Shown to customers who choose bank transfer, and included in their order email.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={lbl}>Account Name</label><input value={bank.account_name || ""} onChange={setB("account_name")} className={inp} data-testid="bank-account-name" /></div>
          <div><label className={lbl}>BSB</label><input value={bank.bsb || ""} onChange={setB("bsb")} className={inp} placeholder="000-000" data-testid="bank-bsb" /></div>
          <div><label className={lbl}>Account Number</label><input value={bank.account_number || ""} onChange={setB("account_number")} className={inp} data-testid="bank-account-number" /></div>
        </div>
        <div><label className={lbl}>Payment Reference</label><input value={bank.reference || ""} onChange={setB("reference")} className={inp} placeholder="Leave blank to use the order number" data-testid="bank-reference" /></div>
        <div><label className={lbl}>Instructions (optional)</label><textarea rows={3} value={bank.instructions || ""} onChange={setB("instructions")} className={inp} placeholder="e.g. Please email your receipt to orders@…" data-testid="bank-instructions" /></div>
      </div>

      <button onClick={() => saveContent("payments", d, "Payment settings updated")} data-testid="pay-save"
        className="inline-flex items-center gap-2 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4"><Save className="w-4 h-4" /> Save Payment Settings</button>
    </div>
  );
}

export default function AdminContent() {
  return (
    <div>
      <h1 className="font-serif text-3xl text-pitch mb-8">Site Content</h1>
      <Tabs defaultValue="homepage">
        <TabsList className="bg-white border border-linen mb-6">
          <TabsTrigger value="homepage" data-testid="content-tab-homepage">Homepage</TabsTrigger>
          <TabsTrigger value="faq" data-testid="content-tab-faq">FAQ</TabsTrigger>
          <TabsTrigger value="pages" data-testid="content-tab-pages">Pages & Legal</TabsTrigger>
          <TabsTrigger value="shipping" data-testid="content-tab-shipping">Shipping</TabsTrigger>
          <TabsTrigger value="payments" data-testid="content-tab-payments">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="homepage"><HomepageEditor /></TabsContent>
        <TabsContent value="faq"><FAQEditor /></TabsContent>
        <TabsContent value="pages"><PagesEditor /></TabsContent>
        <TabsContent value="shipping"><ShippingEditor /></TabsContent>
        <TabsContent value="payments"><PaymentsEditor /></TabsContent>
      </Tabs>
    </div>
  );
}
